import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../shared/TailwindComponents/Button';
import {
  PiCheckSquareLight,
  PiSquareLight,
  PiSpinnerGapLight,
} from 'react-icons/pi';
import Spinner from '../shared/TailwindComponents/Spinner';

interface DeleteTemplateConfirmationProps {
  count?: number;
  entityLabel?: string;          // e.g. "playbook", "template", "table column template"
  entityLabelPlural?: string;    // e.g. "playbooks", "templates"
  description?: string;          // Custom body text; auto-generated if omitted
  showDeleteSessions?: boolean;  // Show "also delete review sessions" checkbox (playbooks only)
  onConfirm: (deleteSessions?: boolean) => Promise<void> | void;
  onCancel: () => void;
}

const DeleteTemplateConfirmation: React.FC<DeleteTemplateConfirmationProps> = ({
  count = 1,
  entityLabel = 'template',
  entityLabelPlural,
  description,
  showDeleteSessions = false,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteSessions, setDeleteSessions] = useState(false);
  const isPlural = count > 1;
  const plural = entityLabelPlural || `${entityLabel}s`;

  const handleConfirm = async () => {
    setIsDeleting(true);
    try {
      await onConfirm(showDeleteSessions ? deleteSessions : undefined);
    } catch {
      setIsDeleting(false);
    }
  };

  if (isDeleting) {
    return (
      <div className="flex flex-col items-center justify-center py-10 px-4 gap-4">
        <div className="relative w-12 h-12">
          <Spinner size="glyph" className="!h-12 !w-12" />
        </div>
        <div className="text-center space-y-1.5">
          <p className="text-sm font-medium text-fg-strong">
            {isPlural
              ? t(`Deleting ${count} ${plural}...`)
              : t(`Deleting ${entityLabel}...`)}
          </p>
          <p className="text-xs text-fg-muted">
            {t('Please wait, this may take a moment.')}
          </p>
        </div>
      </div>
    );
  }

  const defaultDescription = isPlural
    ? t(`This will permanently delete the selected ${plural}. This action cannot be undone.`)
    : t(`This will permanently delete this ${entityLabel}. This action cannot be undone.`);

  return (
    <div className="flex flex-col gap-4">
      <div className="p-4 space-y-3">
        <p className="text-sm text-fg-body">
          {description || defaultDescription}
        </p>

        {showDeleteSessions && (
          <label
            className="flex items-start gap-3 p-3 rounded-lg border border-hairline hover:bg-surface-soft cursor-pointer transition-colors"
            onClick={() => setDeleteSessions(!deleteSessions)}
          >
            <div className="mt-0.5 flex-shrink-0">
              {deleteSessions ? (
                <PiCheckSquareLight className="w-5 h-5 text-danger-500" />
              ) : (
                <PiSquareLight className="w-5 h-5 text-fg-muted" />
              )}
            </div>
            <div>
              <p className="text-sm text-fg-strong">
                {t('Also delete all review sessions')}
              </p>
              <p className="text-xs text-fg-muted mt-0.5">
                {t('Review sessions linked to this playbook will be soft-deleted. You can restore them later if needed.')}
              </p>
            </div>
          </label>
        )}
      </div>

      <div className="flex justify-end space-x-4 pt-6 mt-6 border-t border-hairline px-4">
        <Button variant="neutral" size="small" onClick={onCancel} disabled={isDeleting} className="w-24">
          {t('Cancel')}
        </Button>
        <Button
          variant="destructive"
          size="small"
          onClick={handleConfirm}
          disabled={isDeleting}
          className="w-24"
        >
          {t('Delete')}
        </Button>
      </div>
    </div>
  );
};

export default DeleteTemplateConfirmation;
